import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogIn, RefreshCw, Smartphone, UserRound } from "lucide-react";

import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { clearOnboardingClientState, getLocalDevBootstrap } from "@/lib/api";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";
import { ensureAnonymousSession, resetBrowserSupabaseSession } from "@/lib/supabase-session";

export default function DevOnboarding() {
  const navigate = useNavigate();
  const [bootstrap, setBootstrap] = useState(null);
  const [error, setError] = useState("");
  const [pendingAction, setPendingAction] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadBootstrap() {
      try {
        const payload = await getLocalDevBootstrap();
        if (!cancelled) {
          setBootstrap(payload);
        }
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : "Could not load the local test launcher.");
        }
      }
    }

    void loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartNewUser = async () => {
    if (!bootstrap?.newUser?.path || pendingAction) {
      return;
    }
    setPendingAction("new-user");
    setError("");
    try {
      await resetBrowserSupabaseSession();
      clearOnboardingClientState();
      await ensureAnonymousSession();
      navigate(bootstrap.newUser.path);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Could not start the new-user flow.");
      setPendingAction("");
    }
  };

  const handleStartLinkOnly = async () => {
    if (pendingAction) {
      return;
    }
    setPendingAction("link-only");
    setError("");
    try {
      await resetBrowserSupabaseSession();
      clearOnboardingClientState();
      await ensureAnonymousSession();
      navigate(bootstrap?.linkPath || "/link");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Could not open the mock link flow.");
      setPendingAction("");
    }
  };

  const handleSeedSignIn = async (seedUser, nextPath) => {
    if (!seedUser || pendingAction) {
      return;
    }
    const client = getSupabaseBrowserClient();
    if (!client) {
      setError("Supabase is not configured.");
      return;
    }
    setPendingAction(`${seedUser.key}:${nextPath}`);
    setError("");
    try {
      await resetBrowserSupabaseSession();
      clearOnboardingClientState();
      const { error: signInError } = await client.auth.signInWithPassword({
        email: seedUser.email,
        password: seedUser.password,
      });
      if (signInError) {
        throw signInError;
      }
      navigate(seedUser.paths?.[nextPath] || "/auth");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Could not sign in with the local seed user.");
      setPendingAction("");
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background px-5 py-8">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <div className="flex items-center justify-between gap-3">
          <Logo size="sm" />
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            onClick={() => window.location.reload()}
            disabled={Boolean(pendingAction)}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh fixtures
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="font-display text-3xl font-bold">Local test launcher</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Use this page to test Min locally without WAHA. It seeds a mock share-link flow plus existing users you can sign in with.
          </p>
          {bootstrap && !bootstrap.mockLinkingEnabled ? (
            <p className="text-sm text-destructive">
              Mock linking is off. Start the stack through `scripts/e2e/run_with_local_stack.sh` so `/link` uses the safe local mock flow.
            </p>
          ) : null}
        </div>

        {!bootstrap ? (
          <div className="flex min-h-40 items-center justify-center rounded-3xl border border-border bg-card">
            <div className="inline-flex items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading local test data…
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <section className="rounded-3xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold">New-user flow</h2>
                  <p className="text-sm text-muted-foreground">Share link, link, email, preferences, pricing.</p>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  type="button"
                  className="h-11 w-full rounded-xl"
                  onClick={() => void handleStartNewUser()}
                  disabled={Boolean(pendingAction) || !bootstrap.mockLinkingEnabled}
                >
                  {pendingAction === "new-user" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Opening onboarding…
                    </>
                  ) : (
                    "Start new-user onboarding"
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full rounded-xl"
                  onClick={() => void handleStartLinkOnly()}
                  disabled={Boolean(pendingAction) || !bootstrap.mockLinkingEnabled}
                >
                  {pendingAction === "link-only" ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Opening link flow…
                    </>
                  ) : (
                    "Open mock link only"
                  )}
                </Button>
              </div>
            </section>

            <section className="rounded-3xl border border-border bg-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <UserRound className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-semibold">Seeded existing users</h2>
                  <p className="text-sm text-muted-foreground">Use these to test setup and billing without going through onboarding.</p>
                </div>
              </div>
              <div className="space-y-3">
                {bootstrap.seedUsers?.map((seedUser) => (
                  <div key={seedUser.key} className="rounded-2xl border border-border/80 bg-muted/30 p-4">
                    <div className="mb-3">
                      <p className="font-medium">{seedUser.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{String(seedUser.plan || "").toUpperCase()} plan</p>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Email:</span> <span className="font-mono">{seedUser.email}</span></p>
                      <p><span className="text-muted-foreground">Password:</span> <span className="font-mono">{seedUser.password}</span></p>
                    </div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 rounded-xl"
                        onClick={() => void handleSeedSignIn(seedUser, "setup")}
                        disabled={Boolean(pendingAction)}
                      >
                        {pendingAction === `${seedUser.key}:setup` ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in…
                          </>
                        ) : (
                          "Open setup"
                        )}
                      </Button>
                      <Button
                        type="button"
                        className="h-10 rounded-xl"
                        onClick={() => void handleSeedSignIn(seedUser, "billing")}
                        disabled={Boolean(pendingAction)}
                      >
                        {pendingAction === `${seedUser.key}:billing` ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Signing in…
                          </>
                        ) : (
                          <>
                            <LogIn className="mr-2 h-4 w-4" />
                            Open billing
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        <div className="rounded-3xl border border-border/80 bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>Manual quick checks:</p>
          <p>`/link` should show `Getting code…` on the tapped CTA and keep sibling controls disabled.</p>
          <p>`/setup` pricing should show `Opening checkout…` on the CTA before redirect.</p>
          <p>`/billing` should lock the other plan buttons while checkout is opening.</p>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>
    </div>
  );
}
